import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import { Box, Card, CircularProgress, Link as MuiLink } from '@material-ui/core'
import { useFormik } from 'formik'
import { toast } from 'utils/notify'
import { useDispatch } from 'react-redux'
import { colors } from 'utils/colors'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { cloudFunctionsInstance } from 'config/axios'
import { signInWithCustomToken } from 'store/auth'
import { getConsoleHome } from '../../routes'
import SetNewPasswordCard, { setPasswordSchema } from 'components/SetNewPasswordCard'

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

const SetPassword = () => {
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const query = useQuery()
  const token = query.get('token')

  const [email, setEmail] = useState('')
  const [validToken, setValidToken] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [performingAction, setPerformingAction] = useState(false)

  useEffect(() => {
    const verifySetupToken = async () => {
      if (!token) {
        setVerifying(false)
        return
      }

      try {
        const response = await cloudFunctionsInstance.get('/verifySetupToken', {
          params: { token }
        })
        if (response.data.valid) {
          setEmail(response.data.email)
          setValidToken(true)
        }
      } catch (error) {
        console.error('Error verifying setup token:', error)
      } finally {
        setVerifying(false)
      }
    }

    verifySetupToken()
  }, [token])

  const onSubmit = async (values) => {
    try {
      setPerformingAction(true)
      const response = await cloudFunctionsInstance.post('/setNewPassword', {
        token,
        newPassword: values.password
      })
      const result = await dispatch(signInWithCustomToken(response.data.customToken))
      if (result.error) {
        toast.error(result.error)
        history.replace('/')
        return
      }
      toast.success('Password set. Welcome!')
      if (result.needsPicker) {
        history.push('/select-conference', {
          partialUser: result.partialUser,
          assignments: result.assignments
        })
        return
      }
      history.push(getConsoleHome(result.user))
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Could not set your password. Please try again.')
    } finally {
      setPerformingAction(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: setPasswordSchema,
    onSubmit: onSubmit
  })

  if (verifying) {
    return (
      <Box className={classes.container}>
        <Card className={classes.card}>
          <Box display="flex" justifyContent="center" mb={2}>
            <CircularProgress />
          </Box>
          <Typography variant="body1" align="center">
            Verifying your activation link...
          </Typography>
        </Card>
      </Box>
    )
  }

  if (!validToken) {
    return (
      <Box className={classes.container}>
        <Card className={classes.card}>
          <Typography variant="h5" className={classes.header}>
            Invalid Activation Link
          </Typography>
          <Typography variant="body2" align="center" className={classes.subHeader}>
            This activation link is invalid or has expired. Contact your administrator for a new
            one.
          </Typography>
          <Box textAlign="center">
            <MuiLink component={Link} to="/" className={classes.backLink}>
              Back to Login
            </MuiLink>
          </Box>
        </Card>
      </Box>
    )
  }

  return (
    <SetNewPasswordCard
      email={email}
      formik={formik}
      submitting={performingAction}
      onBack={() => history.replace('/')}
    />
  )
}

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    padding: '20px'
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    borderRadius: '16px',
    padding: '35px 40px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
  },
  header: {
    textAlign: 'center',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: colors.indigo
  },
  subHeader: {
    marginBottom: '24px',
    color: '#64748b'
  },
  backLink: {
    cursor: 'pointer',
    color: colors.indigo,
    fontWeight: 600,
    fontSize: '0.875rem'
  }
})

export default SetPassword
